/**
 * useBehaviorAnalytics Hook - Phase 2C-4
 *
 * Fetches behavioral analytics data for admin dashboard.
 * BSL Compliant: Data only, no interpretive labels.
 */

import { useState, useEffect, useCallback } from 'react';
import { BehaviorService, BehaviorSummary, UserBehavior, ChartData } from '../api';

interface UseBehaviorAnalyticsReturn {
  // Summary data
  summary: BehaviorSummary | null;
  summaryLoading: boolean;
  summaryError: string | null;
  
  // User list
  users: UserBehavior[];
  usersLoading: boolean;
  usersError: string | null;
  
  // Selected user detail
  selectedUser: UserBehavior | null;
  selectedUserLoading: boolean;
  
  // Chart data
  decisionTimeData: ChartData | null;
  stakeIntensityData: ChartData | null;
  lockPressureData: ChartData | null;
  chartsLoading: boolean;
  
  // Actions
  selectUser: (userId: string | null) => void;
  refetch: () => void;
}

export function useBehaviorAnalytics(poolId: string | null): UseBehaviorAnalyticsReturn {
  // Summary state
  const [summary, setSummary] = useState<BehaviorSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  
  // Users state
  const [users, setUsers] = useState<UserBehavior[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  
  // Selected user state
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserBehavior | null>(null);
  const [selectedUserLoading, setSelectedUserLoading] = useState(false);
  
  // Chart data state
  const [decisionTimeData, setDecisionTimeData] = useState<ChartData | null>(null);
  const [stakeIntensityData, setStakeIntensityData] = useState<ChartData | null>(null);
  const [lockPressureData, setLockPressureData] = useState<ChartData | null>(null);
  const [chartsLoading, setChartsLoading] = useState(false);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    if (!poolId) return;
    
    setSummaryLoading(true);
    setSummaryError(null);
    
    try {
      const data = await BehaviorService.getSummary(poolId);
      setSummary(data);
    } catch (err) {
      setSummaryError(err instanceof Error ? err.message : 'Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  }, [poolId]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    if (!poolId) return;
    
    setUsersLoading(true);
    setUsersError(null);
    
    try {
      const data = await BehaviorService.getPoolUsers(poolId, { limit: 50 });
      setUsers(data);
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, [poolId]);

  // Fetch chart data
  const fetchChartData = useCallback(async () => {
    if (!poolId) return;
    
    setChartsLoading(true);
    
    try {
      const [decisionTime, stakeIntensity, lockPressure] = await Promise.all([
        BehaviorService.getDecisionTimeData(poolId, selectedUserId || undefined),
        BehaviorService.getStakeIntensityData(poolId, selectedUserId || undefined),
        BehaviorService.getLockPressureData(poolId, selectedUserId || undefined),
      ]);
      
      setDecisionTimeData(decisionTime);
      setStakeIntensityData(stakeIntensity);
      setLockPressureData(lockPressure);
    } catch (err) {
      console.error('Failed to load chart data:', err);
    } finally {
      setChartsLoading(false);
    }
  }, [poolId, selectedUserId]);

  // Fetch selected user detail
  const fetchSelectedUser = useCallback(async () => {
    if (!poolId || !selectedUserId) {
      setSelectedUser(null);
      return;
    }
    
    setSelectedUserLoading(true);
    
    try {
      const data = await BehaviorService.getUserBehavior(poolId, selectedUserId);
      setSelectedUser(data);
    } catch (err) {
      console.error('Failed to load user detail:', err);
      setSelectedUser(null);
    } finally {
      setSelectedUserLoading(false);
    }
  }, [poolId, selectedUserId]);

  // Initial fetch when poolId changes
  useEffect(() => {
    if (poolId) {
      fetchSummary();
      fetchUsers();
      fetchChartData();
    } else {
      setSummary(null);
      setUsers([]);
      setDecisionTimeData(null);
      setStakeIntensityData(null);
      setLockPressureData(null);
    }
  }, [poolId, fetchSummary, fetchUsers, fetchChartData]);

  // Fetch selected user when selection changes
  useEffect(() => {
    fetchSelectedUser();
    fetchChartData(); // Refetch charts for selected user
  }, [selectedUserId, fetchSelectedUser, fetchChartData]);

  // Select user action
  const selectUser = useCallback((userId: string | null) => {
    setSelectedUserId(userId);
  }, []);

  // Refetch all data
  const refetch = useCallback(() => {
    fetchSummary();
    fetchUsers();
    fetchChartData();
  }, [fetchSummary, fetchUsers, fetchChartData]);

  return {
    summary,
    summaryLoading,
    summaryError,
    users,
    usersLoading,
    usersError,
    selectedUser,
    selectedUserLoading,
    decisionTimeData,
    stakeIntensityData,
    lockPressureData,
    chartsLoading,
    selectUser,
    refetch,
  };
}